import { TOOL_NAMES } from 'js/src/agent/toolNames';
import { z } from 'zod';

import { type EnhancedToolResult, type ToolExecutionContext } from '../types';
import { Tool } from './Tool';

import { ApiService } from 'js/src/client';
import { getCanUseAIAvatar } from 'js/src/data/account/account-selectors';
import {
    getAIAvatarsOrm,
    getAIAvatarVoicePresetByAvatarId,
    setAIAvatars,
    setAIAvatarVoicePresets,
} from 'js/src/data/ai-voices-and-avatars';

export const listAvatarsSchema = z.object({});
export type ListAvatarsArgs = z.infer<typeof listAvatarsSchema>;

export class ListAvatarsTool extends Tool<typeof listAvatarsSchema> {
    protected override readonly schema = listAvatarsSchema;
    protected override readonly toolName = TOOL_NAMES.listAvatars;
    protected override readonly toolDescription = `List the AI avatars available to this team — the on-screen presenters an avatar scene group can use. Each result includes the avatar's id, name, variant, and the voice paired with it. Pass the chosen id as \`avatar_id\` to ${TOOL_NAMES.addAvatarSceneGroup}.`;

    public override getToolDisplayName(): string {
        return 'Listing avatars';
    }

    public get statusIcon(): string {
        return 'face';
    }

    public async execute(
        _args: ListAvatarsArgs,
        _context: ToolExecutionContext,
    ): Promise<EnhancedToolResult> {
        if (!getCanUseAIAvatar(this.getState())) {
            return { result: 'AI avatars are not available on this account.', isSuccess: false };
        }

        if (getAIAvatarsOrm(this.getState()).isEmpty()) {
            const [avatarsResponse, presetsResponse] = await Promise.all([
                ApiService.aiAvatarList(),
                ApiService.activeAiAvatarVoicePresetList(),
            ]);
            this.dispatch(setAIAvatars(avatarsResponse.data));
            this.dispatch(setAIAvatarVoicePresets(presetsResponse.data));
        }

        const orm = getAIAvatarsOrm(this.getState());
        const ids = orm.getAllIds().toArray();
        if (ids.length === 0) {
            return { result: 'No AI avatars are available for this account.', isSuccess: true };
        }

        const lines = ids.map((id, index) => {
            const avatar = orm.getById(id);
            const preset = getAIAvatarVoicePresetByAvatarId(this.getState(), id);
            const voice = preset?.get('voice_slug');
            const voiceText = voice ? `  voice=${voice}` : '';
            return `[${index + 1}] id=${avatar.get('id')}  "${avatar.get('name')}"  variant=${avatar.get('variant')}${voiceText}`;
        });

        return {
            result:
                `Found ${lines.length} avatar${lines.length === 1 ? '' : 's'}:\n` +
                lines.join('\n') +
                `\n\nPass the chosen id as \`avatar_id\` to ${TOOL_NAMES.addAvatarSceneGroup}.`,
            isSuccess: true,
        };
    }
}
